/**
 * Click-to-fill hint for the custom color field.
 *
 * When the cart already holds custom colors, offer them under the color
 * input so a customer ordering more of the same color types the same string.
 * Color identity is a conservative string match, so this is what keeps the
 * normalizer from having to adjudicate near-misses.
 *
 * It only fills a text input. It never touches a price or an OPC selection —
 * the fee is still derived from the cart afterwards by the reconciler.
 */
(function (root, factory) {
  'use strict';
  const isNode = typeof module !== 'undefined' && module.exports;
  const model = isNode ? require('./custom-color-model.js') : root.CustomColorModel;
  const constants = isNode
    ? require('./custom-color-constants.js')
    : root.CustomColorConstants;
  const api = factory(model, constants);
  if (isNode) {
    module.exports = api;
    return;
  }
  root.CustomColorHint = api;
  api.mount();
})(typeof globalThis !== 'undefined' ? globalThis : this, function (model, C) {
  'use strict';

  const HINT_ID = 'custom-color-hint';

  /** Pure: what the hint should say, or null if there is nothing to offer. */
  function buildHint(cartLines, strings) {
    const colors = model.distinctColors(cartLines || []);
    if (!colors.length) return null;
    return { colors: colors, label: strings.alreadyInOrder };
  }

  // Customer-facing copy lives in locales/en.default.json under
  // `cart.custom_color_already_in_order`, rendered into theme.strings by
  // layout/theme.liquid — the convention this theme already uses for JS
  // strings (see opc-budget-ui.js). theme.strings is a flat object of
  // camelCase names, not nested by locale group, so the key to read here is
  // `theme.strings.customColorAlreadyInOrder`.
  //
  // The literal below is a fallback, not the source of truth. It exists so a
  // missing key renders real copy instead of "undefined" in front of a
  // customer, and so this module stays testable under node with no theme.
  function strings() {
    const themed = window.theme && window.theme.strings;
    return {
      alreadyInOrder:
        (themed && themed.customColorAlreadyInOrder) || 'Already in your order:',
    };
  }

  /** Is this the element a customer actually types into? */
  function isInputLike(el) {
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toUpperCase();
    if (tag === 'TEXTAREA') return true;
    if (tag !== 'INPUT') return false;
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    // A hidden mirror input is exactly what this used to attach to.
    return type !== 'hidden';
  }

  /**
   * The visible color field, located by the class the merchant puts on it.
   *
   * The options app gives us nothing stable of its own — no name attributes,
   * and per-option hash ids that change when a set is rebuilt — so the class
   * is the contract. See COLOR_INPUT_CLASS.
   *
   * The class may sit on the field or on its wrapper, depending on where the
   * app applies it, so accept either. Returns null when it is absent, and the
   * hint then does not render at all: no hint is fine, a hint wired to the
   * wrong element is not.
   */
  function colorInput() {
    const marked = document.querySelectorAll('.' + C.COLOR_INPUT_CLASS);
    for (let i = 0; i < marked.length; i += 1) {
      const el = marked[i];
      if (isInputLike(el)) return el;
      const inner = el.querySelector('textarea, input');
      if (isInputLike(inner)) return inner;
    }
    return null;
  }

  function render(hint, input) {
    const existing = document.getElementById(HINT_ID);
    if (existing) existing.remove();
    if (!hint) return;

    const wrap = document.createElement('div');
    wrap.id = HINT_ID;
    // No size class: inherit the body size. It carried .small-text
    // (var(--smaller-text-size-1)) and read too quietly next to the field it
    // belongs to. Deliberately not a hardcoded px value — a static .css file
    // cannot see the theme's Liquid font settings, so a literal here would
    // stop tracking the merchant's base size the moment it changed.

    const label = document.createElement('span');
    label.textContent = hint.label + ' ';
    wrap.appendChild(label);

    hint.colors.forEach(function (color, index) {
      if (index > 0) wrap.appendChild(document.createTextNode(', '));
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'underline';
      button.textContent = color.display;
      button.addEventListener('click', function () {
        input.value = color.display;
        // OPC watches its own inputs; a plain assignment is invisible to it.
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrap.appendChild(button);
    });

    input.parentNode.appendChild(wrap);
  }

  function refresh() {
    const input = colorInput();
    if (!input) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(buildHint((cart && cart.items) || [], strings()), input);
      })
      .catch(function () {
        // A missing hint costs a customer nothing. Stay quiet.
      });
  }

  // Nothing in this theme fires when OPC finishes rendering its fields, so
  // DOMContentLoaded and the cart events above can both fire before the
  // color input exists — a real, silent miss for the one page view that
  // matters most. Watch a stable ancestor for the field to appear instead.
  // This is a one-shot "wait for it to exist", not a live sync: once found,
  // it disconnects and lets the cart-event refreshes keep the hint current.
  const OBSERVE_TIMEOUT_MS = 10000;

  function watchForInput() {
    if (typeof MutationObserver === 'undefined') return;
    if (colorInput()) return; // already there; nothing to wait for
    const container = document.querySelector('product-form') || document.body;
    if (!container) return;

    let done = false;
    const observer = new MutationObserver(function () {
      if (done || !colorInput()) return;
      stop();
      refresh();
    });

    function stop() {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
    }

    observer.observe(container, { childList: true, subtree: true });

    // A field that never appears is the pre-existing behaviour: no hint, no
    // harm. An observer that never disconnects is not — OPC's client-side
    // render has had ample time by 10s, so stop watching regardless.
    const timer = setTimeout(stop, OBSERVE_TIMEOUT_MS);
  }

  function mount() {
    // OPC renders its fields client-side, so the input may not exist yet at
    // DOMContentLoaded. We also refresh on OPC's own cart events, and refresh
    // once immediately in case the input is already present (e.g. on a
    // client-side navigation after scripts are already loaded).
    document.addEventListener('DOMContentLoaded', refresh);
    document.addEventListener('on:cart:add', refresh);
    document.addEventListener('on:cart:change', refresh);
    refresh();
    watchForInput();
  }

  return { buildHint: buildHint, isInputLike: isInputLike, mount: mount };
});
